import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createClient } from '@/lib/supabase/client'
import { useAuth } from '@/lib/auth'
import type {
  Series,
  SeriesWithCount,
  CreateSeriesInput,
  UpdateSeriesInput,
} from '@/types/series'

/**
 * Hook to fetch all series for the current team with episode counts
 */
export function useSeries() {
  const supabase = createClient()
  const { membership } = useAuth()

  return useQuery({
    queryKey: ['series', membership?.team_id],
    queryFn: async (): Promise<SeriesWithCount[]> => {
      if (!membership?.team_id) return []

      // Fetch series with episode count using RPC function
      const { data, error } = await supabase
        .from('series')
        .select('*')
        .eq('team_id', membership.team_id)
        .order('created_at', { ascending: true })

      if (error) throw error

      // Get episode counts for each series
      const seriesWithCounts = await Promise.all(
        (data as Series[]).map(async (s) => {
          const { count } = await supabase
            .from('episodes')
            .select('*', { count: 'exact', head: true })
            .eq('series_id', s.id)

          return {
            ...s,
            episode_count: count ?? 0,
          }
        })
      )

      return seriesWithCounts
    },
    enabled: !!membership?.team_id,
  })
}

/**
 * Hook to fetch a single series by ID
 */
export function useSeriesById(seriesId: string | undefined) {
  const supabase = createClient()

  return useQuery({
    queryKey: ['series', seriesId],
    queryFn: async (): Promise<SeriesWithCount | null> => {
      if (!seriesId) return null

      const { data, error } = await supabase
        .from('series')
        .select('*')
        .eq('id', seriesId)
        .single()

      if (error) throw error

      // Get episode count
      const { count } = await supabase
        .from('episodes')
        .select('*', { count: 'exact', head: true })
        .eq('series_id', seriesId)

      return {
        ...(data as Series),
        episode_count: count ?? 0,
      }
    },
    enabled: !!seriesId,
  })
}

/**
 * Hook to create a new series
 */
export function useCreateSeries() {
  const supabase = createClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateSeriesInput): Promise<Series> => {
      const { data, error } = await supabase
        .from('series')
        .insert(input as never)
        .select()
        .single()

      if (error) throw error

      // Atomically increment series_used counter via RPC
      const {
        data: { user },
      } = await supabase.auth.getUser()

      if (user) {
        const { error: rpcError } = await supabase.rpc(
          'increment_series_used' as never,
          { p_user_id: user.id } as never
        )
        if (rpcError) {
          console.error('Failed to increment series counter:', rpcError)
          // Don't throw here - series was created successfully
        }
      }

      return data as Series
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['series', variables.team_id] })
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
    },
  })
}

/**
 * Hook to update an existing series
 */
export function useUpdateSeries() {
  const supabase = createClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: UpdateSeriesInput & { id: string }): Promise<Series> => {
      const { data, error } = await supabase
        .from('series')
        .update(input as never)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as Series
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['series', data.team_id] })
      queryClient.invalidateQueries({ queryKey: ['series', data.id] })
    },
  })
}

/**
 * Hook to delete a series
 */
export function useDeleteSeries() {
  const supabase = createClient()
  const queryClient = useQueryClient()
  const { membership } = useAuth()

  return useMutation({
    mutationFn: async (seriesId: string): Promise<void> => {
      const { error } = await supabase.from('series').delete().eq('id', seriesId)

      if (error) throw error

      // Atomically decrement series_used counter via RPC
      const {
        data: { user },
      } = await supabase.auth.getUser()

      if (user) {
        await supabase.rpc(
          'decrement_series_used' as never,
          { p_user_id: user.id } as never
        )
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['series', membership?.team_id],
      })
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
    },
  })
}

/**
 * Hook to check if user can add an episode to a specific series
 * Calls the RPC function to check per-series episode limit
 */
export function useCanAddEpisodeToSeries(seriesId: string | undefined) {
  const supabase = createClient()

  return useQuery({
    queryKey: ['canAddEpisode', seriesId],
    queryFn: async (): Promise<boolean> => {
      if (!seriesId) return false

      const {
        data: { user },
      } = await supabase.auth.getUser()

      if (!user) return false

      const { data, error } = await supabase.rpc(
        'can_add_episode_to_series' as never,
        { p_user_id: user.id, p_series_id: seriesId } as never
      )

      if (error) {
        console.error('Error checking episode limit:', error)
        return false
      }

      return data as boolean
    },
    enabled: !!seriesId,
  })
}

/**
 * Hook to get the next episode number for a series
 */
export function useNextEpisodeNumber(seriesId: string | undefined) {
  const supabase = createClient()

  return useQuery({
    queryKey: ['nextEpisodeNumber', seriesId],
    queryFn: async (): Promise<number> => {
      if (!seriesId) return 1

      const { data, error } = await supabase.rpc(
        'get_next_episode_number' as never,
        { p_series_id: seriesId } as never
      )

      if (error) {
        console.error('Error getting next episode number:', error)
        return 1
      }

      return data as number
    },
    enabled: !!seriesId,
  })
}
